/**
 * Hook for inserting comments into the order-comments Postgres table.
 * @module useCommentsInsert
 */

import { useCallback, useEffect } from "react";

import { StatusLogData } from "../../data-model/statusLogModel";
import {
  singleOrderDetailsGenericInsert,
  SingleOrderDetailsGenericInsertResponse,
} from "../../../shared/api/postgreSQL/postCalls/singleOrderDetailsGenericInsert";
import { SingleOrderDetailsTableName } from "../../../shared/enums/singleOrderDetailsTableName";
import { useAbortableFetch } from "../useAbortableFetch";

/** Parameters for the {@link useCommentsInsert} hook. */
interface UseCommentsInsertProps {
  /** PA order ID to insert the comment for. */
  orderId: string;
  /** Organization ID scoping the query. */
  orgId: string;
}

/**
 * Provides an abortable insert function for adding comments to a single order in Postgres.
 *
 * Does not auto-fetch on mount. Instead it exposes a `runInsert(data)` function
 * that callers invoke with the comment payload.
 * In-flight requests are automatically cancelled on unmount.
 *
 * @param props - Order ID and org ID identifying the target order.
 * @returns runInsert - Triggers the insert with the given comment data.
 * @returns abort     - Manually cancel any in-flight insert request.
 * @returns data      - The insert response (null until a request completes).
 * @returns loading   - Whether an insert request is in progress.
 * @returns error     - Error object if the insert failed.
 */
export const useCommentsInsert = (props: UseCommentsInsertProps) => {
  const { orderId, orgId } = props;

  const fetchFn = useCallback(
    async (signal: AbortSignal, commentData: StatusLogData) => {
      return singleOrderDetailsGenericInsert<StatusLogData>(
        {
          order_id: orderId,
          org_id: orgId,
          table_name: SingleOrderDetailsTableName.ORDER_COMMENTS,
          data: commentData,
        },
        signal,
      );
    },
    [orderId, orgId],
  );

  const { runFetch, abort, data, loading, error } =
    useAbortableFetch<SingleOrderDetailsGenericInsertResponse>(fetchFn);

  useEffect(() => {
    return () => abort();
  }, [abort]);

  const runInsert = useCallback(
    async (commentData: StatusLogData) => runFetch(commentData),
    [runFetch],
  );

  return { runInsert, abort, data, loading, error };
};
